import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { User, Mail, Phone, Shield, Lock, Save, Crown } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminProfile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const roleLabels = {
    super_admin: { name: 'Super Administrateur', icon: Crown, color: 'bg-purple-100 text-purple-800' },
    admin: { name: 'Administrateur', icon: Shield, color: 'bg-blue-100 text-blue-800' },
    manager: { name: 'Gestionnaire', icon: User, color: 'bg-green-100 text-green-800' }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/management/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(response.data);
      setForm({
        full_name: response.data.full_name,
        email: response.data.email,
        phone: response.data.phone || '',
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Erreur lors du chargement du profil');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();

    // Validate password change
    if (form.new_password) {
      if (!form.current_password) {
        toast.error('Veuillez entrer votre mot de passe actuel');
        return;
      }
      if (form.new_password !== form.confirm_password) {
        toast.error('Les mots de passe ne correspondent pas');
        return;
      }
      if (form.new_password.length < 6) {
        toast.error('Le nouveau mot de passe doit contenir au moins 6 caractères');
        return;
      }
    }

    try {
      const token = localStorage.getItem('admin_token');
      const formData = new FormData();
      
      if (form.full_name !== profile.full_name) formData.append('full_name', form.full_name);
      if (form.email !== profile.email) formData.append('email', form.email);
      if (form.phone !== (profile.phone || '')) formData.append('phone', form.phone);
      if (form.current_password) formData.append('current_password', form.current_password);
      if (form.new_password) formData.append('new_password', form.new_password);

      await axios.patch(`${API}/admin/management/profile`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Profil mis à jour avec succès');
      
      // Update local storage if email changed
      if (form.email !== profile.email) {
        localStorage.setItem('admin_email', form.email);
      }

      setEditing(false);
      setForm({
        ...form,
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
      fetchProfile();
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-slate-500">Chargement...</p>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-slate-500">Profil introuvable</p>
      </div>
    );
  }

  const roleInfo = roleLabels[profile.role];
  const RoleIcon = roleInfo?.icon || Shield;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900 mb-2">Mon Profil</h1>
        <p className="text-slate-600">Gérez vos informations personnelles</p>
      </div>

      {/* Profile Card */}
      <Card>
        <CardHeader className="bg-gradient-to-r from-blue-900 to-blue-800 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="bg-white/20 p-4 rounded-full">
                <User className="h-8 w-8" />
              </div>
              <div>
                <CardTitle className="text-2xl">{profile.full_name}</CardTitle>
                <p className="text-blue-100 mt-1">{profile.email}</p>
              </div>
            </div>
            <Badge className={`${roleInfo?.color} flex items-center gap-2`}>
              <RoleIcon className="h-4 w-4" />
              {roleInfo?.name}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="p-8">
          <form onSubmit={handleSave} className="space-y-6">
            {/* Account Info Section */}
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Informations du compte</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="full_name" className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Nom complet
                  </Label>
                  <Input
                    id="full_name"
                    value={editing ? form.full_name : profile.full_name}
                    onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                    disabled={!editing}
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={editing ? form.email : profile.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    disabled={!editing}
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    Téléphone
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={editing ? form.phone : (profile.phone || '')}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    disabled={!editing}
                    placeholder="+33 6 12 34 56 78"
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label>Rôle</Label>
                  <div className="mt-2 px-3 py-2 bg-slate-50 rounded-md">
                    <span className="text-slate-900 font-medium">{roleInfo?.name}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Password Change Section */}
            {editing && (
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Lock className="h-5 w-5" />
                  Changer le mot de passe
                </h3>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="current_password">Mot de passe actuel</Label>
                    <Input
                      id="current_password"
                      type="password"
                      value={form.current_password}
                      onChange={(e) => setForm({ ...form, current_password: e.target.value })}
                      placeholder="Requis pour changer le mot de passe"
                      className="mt-2"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="new_password">Nouveau mot de passe</Label>
                      <Input
                        id="new_password"
                        type="password"
                        value={form.new_password}
                        onChange={(e) => setForm({ ...form, new_password: e.target.value })}
                        placeholder="Minimum 6 caractères"
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="confirm_password">Confirmer le mot de passe</Label>
                      <Input
                        id="confirm_password"
                        type="password"
                        value={form.confirm_password}
                        onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
                        placeholder="Répéter le mot de passe"
                        className="mt-2"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Account Details */}
            <div className="border-t pt-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Détails du compte</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-600">Compte créé le</p>
                  <p className="font-medium text-slate-900">{formatDate(profile.created_at)}</p>
                </div>
                <div>
                  <p className="text-slate-600">Dernière connexion</p>
                  <p className="font-medium text-slate-900">{formatDate(profile.last_login)}</p>
                </div>
                <div>
                  <p className="text-slate-600">Statut du compte</p>
                  <Badge className={profile.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                    {profile.is_active ? 'Actif' : 'Inactif'}
                  </Badge>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 pt-4">
              {editing ? (
                <>
                  <Button type="submit" className="bg-blue-900 hover:bg-blue-800">
                    <Save className="h-4 w-4 mr-2" />
                    Enregistrer les modifications
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setEditing(false);
                      fetchProfile();
                    }}
                  >
                    Annuler
                  </Button>
                </>
              ) : (
                <Button
                  type="button"
                  onClick={() => setEditing(true)}
                  className="bg-blue-900 hover:bg-blue-800"
                >
                  Modifier le profil
                </Button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminProfile;
